import { isAuthError, reportAuthFailure } from '@/shared/api/auth-failure';
import { unwrapResult, type ApiResult } from '@/shared/api/result';
import { clientEnv } from '@/shared/config/env';
import { currentLocale } from '@/shared/i18n/messages';
import { reportEvent } from '@/shared/monitoring/monitoring';
import { getDeviceId } from '@/shared/device/device-id';
import { createRequestId } from '@/shared/utils/request-id';
import { metaClickHeaders } from '@/shared/tracking/meta-pixel';
import { protectRequest } from './api-crypto';
import {
  getSimulationApiBaseUrl,
  getSimulationSiteId,
  getSimulationToken,
  shouldUseSimulationApi,
} from '@/shared/simulation/simulation-state';

export interface HttpRequestOptions<TBody = unknown> {
  authToken?: string;
  body?: TBody;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  method?: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT';
  runtimeVersion?: string;
  signal?: AbortSignal;
  simulationBaseUrl?: string;
  skipSimulationToken?: boolean;
}

export class HttpError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
  }
}

const normalizeBase = (baseUrl: string) => baseUrl.replace(/\/$/, '');

const rewriteSimulationSite = <TBody>(body: TBody | undefined, simSiteId: string) => {
  if (!simSiteId || body == null || typeof body !== 'object' || Array.isArray(body) || !('siteId' in body)) {
    return body;
  }
  return { ...body, siteId: simSiteId };
};

const buildUrl = (path: string, baseUrl = clientEnv.apiBaseUrl) =>
  `${normalizeBase(baseUrl)}${path}`;

export const requestJson = async <TResponse, TBody = unknown>(
  path: string,
  options: HttpRequestOptions<TBody> = {},
): Promise<TResponse> => {
  const method = options.method ?? 'GET';
  const useSim =
    !options.skipSimulationToken && !options.simulationBaseUrl && shouldUseSimulationApi(path);
  const simSiteId = useSim ? getSimulationSiteId() : '';
  const body = rewriteSimulationSite(options.body, simSiteId);
  const bodyText = body === undefined ? '' : JSON.stringify(body);
  const baseUrl = options.simulationBaseUrl || (useSim ? getSimulationApiBaseUrl() : clientEnv.apiBaseUrl);
  const requestUrl = new URL(buildUrl(path, baseUrl), window.location.origin);
  if (simSiteId && requestUrl.searchParams.has('siteId')) {
    requestUrl.searchParams.set('siteId', simSiteId);
  }
  const cryptoHeaders = await protectRequest({
    bodyText,
    method,
    path: requestUrl.pathname,
    query: requestUrl.search ? requestUrl.search.slice(1) : '',
  });

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': currentLocale.value,
    'X-App-Version': clientEnv.appVersion,
    'X-Build-Version': clientEnv.buildVersion,
    'X-Client-Type': clientEnv.clientType,
    'X-Device-Id': getDeviceId(),
    'X-Request-Id': createRequestId(),
    'X-Site-Id': simSiteId || clientEnv.siteId,
    ...metaClickHeaders(),
    ...cryptoHeaders.headers,
    ...options.headers,
  };

  if (bodyText) headers['Content-Type'] = 'application/json';
  const authToken = useSim ? getSimulationToken() || options.authToken : options.authToken;
  if (authToken) headers.Authorization = `Bearer ${authToken}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  if (options.runtimeVersion) headers['X-Runtime-Version'] = options.runtimeVersion;

  const startedAt = performance.now();
  const response = await fetch(requestUrl, {
    body: cryptoHeaders.bodyText || undefined,
    headers,
    method,
    signal: options.signal,
  });
  const durationMs = Math.round(performance.now() - startedAt);

  reportEvent({
    context: {
      durationMs,
      path,
      status: response.status,
    },
    event: 'api.request.completed',
    level: response.ok ? 'debug' : 'warn',
  });

  if (!response.ok) {
    const httpError = new HttpError(response.status, `接口请求失败：${response.status}`);
    if (isAuthError(httpError) && !useSim && !options.simulationBaseUrl) reportAuthFailure(httpError);
    throw httpError;
  }

  const result = (await response.json()) as ApiResult<TResponse>;
  try {
    return unwrapResult(result);
  } catch (error) {
    if (isAuthError(error) && !useSim && !options.simulationBaseUrl) reportAuthFailure(error);
    throw error;
  }
};
